import {Component, Injectable, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {UserService} from "../service/user.service";
import {EMAIL_PATTERN} from "../constants/validation.constants";
import {Router} from "@angular/router";
import {AuthenticationResponse} from "../interface/authenticationResponse";
import {UserRole} from "../enums/UserRole";
import {HttpErrorResponse} from "@angular/common/http";
import {FavoriteCardsService} from "../service/favorite-cards.service";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {
  VERIFICATION_ALERT_FOR_ACTIVATING_ACCOUNT
} from "../constants/global.constants";
import {TokenUtil} from "../utilityClass/tokenUtil";
import {NavigationService} from "../service/navigation.service";
import {InvitationTokenService} from "../service/invitation-token.service";

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss', '../shared/scss/login.scss']
})
@Injectable({
  providedIn: 'root'
})
export class LoginPageComponent implements OnInit {

  form: FormGroup;

  showPassword: boolean;
  loginError: boolean = false;
  unregisteredMail: boolean = false;
  notVerifiedMessage: boolean = false;
  verifiedMessage: boolean = false;
  isNavigateToTeamPageAfterLogin: boolean = false;

  email: string;
  favoriteCardId: bigint | null = null;
  favoriteCardCardId: string | null = null;

  phoneNumber: string | null = null;
  countryISO: string | null = null;

  reservedIdentificationId: string | null = null;
  returnUrl: string | null = null;
  fromSubscriptionPage: boolean = false;
  subscriptionInfoId: number | null = null;
  isMonthly: boolean | null = null;
  fromRegistration: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private router: Router,
    private favoriteCardsService: FavoriteCardsService,
    private snackBar: SnackBarUtil,
    private tokenUtil: TokenUtil,
    private navigationService: NavigationService,
    private invitationTokenService: InvitationTokenService,
  ) {
    this.email = router.getCurrentNavigation()?.extras.state?.['email'];
    this.favoriteCardId = router.getCurrentNavigation()?.extras.state?.['favoriteCardId'];
    this.favoriteCardCardId = router.getCurrentNavigation()?.extras.state?.['favoriteCardCardId'];
    this.isNavigateToTeamPageAfterLogin = router.getCurrentNavigation()?.extras.state?.['isNavigateToTeamPageAfterLogin'];
    this.fromSubscriptionPage = router.getCurrentNavigation()?.extras.state?.['fromSubscriptionPage'] || false;
    this.subscriptionInfoId = router.getCurrentNavigation()?.extras.state?.['subscriptionInfoId'] || null;
    this.isMonthly = router.getCurrentNavigation()?.extras.state?.['isMonthly'] ?? null;
    this.fromRegistration = router.getCurrentNavigation()?.extras.state?.['fromRegistration'] || false;
    this.form = this.createLoginForm();
    this.showPassword = false;
    this.notVerifiedMessage = false;
  }

  ngOnInit(): void {
    const userToken = this.userService.token;
    const userRole = this.userService.role;
    this.reservedIdentificationId = history.state.reservedIdentificationId || null;
    this.returnUrl = history.state.returnUrl || null;
    this.fromSubscriptionPage = history.state.fromSubscriptionPage || false;
    this.subscriptionInfoId = history.state.subscriptionInfoId || null;
    this.isMonthly = history.state.isMonthly ?? null;
    this.fromRegistration = history.state.fromRegistration || false;
    if (userToken && userRole != UserRole.ADMIN) {
      this.router.navigate(['/cards']);
    }
  }

  createLoginForm(): FormGroup {
    const formGroup = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
    this.setEmailFromForgotPassword(formGroup);
    return formGroup;
  }

  navigateTo(route: string): void {
    this.navigationService.navigateWithReservedIdAndReturnUrl(route, this.reservedIdentificationId!, this.returnUrl!);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  login() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const user = {
      email: this.lowercaseFirstLetter(this.form.value.email),
      password: this.form.value.password,
      returnSecureToken: true
    };

    this.userService.login(user).subscribe((response: AuthenticationResponse) => {
      const token = response.token
      const email = response.email
      const role = response.role
      if (role === UserRole.USER) {
        this.setUserInfoIntoCookies(token, email, role)
        this.navigationAfterLogin()
      } else {
        this.loginError = true
      }
    }, (error: HttpErrorResponse) => {
      if (error.status === 409) {
        this.loginError = true;
        this.unregisteredMail = false;
      } else if (error.status === 404) {
        this.unregisteredMail = true;
      } else if (error.status === 403) {
        this.handle403Error(user)
      } else if (error.status === 429) {
        this.unregisteredMail = true;
      } else {
        console.error('Login error:', error)
      }
    })
  }

  handle403Error(user: any) {
    this.userService.findByEmail(user.email).subscribe(response => {
      this.phoneNumber = response.phoneNumber;
      this.countryISO = response.countryIso;
      this.navigateToPhoneVerification(this.phoneNumber);
    });
  }

  navigateToPhoneVerification(phoneNumber: string) {
    this.router.navigate(['/verify-phone'], {
      state: {
        userPhoneNumber: phoneNumber
      }
    });
    this.showSnackbar(MockupDataModel.info(VERIFICATION_ALERT_FOR_ACTIVATING_ACCOUNT), SnackBarUtil.CLASSES.info);
  }

  get loginFormControl() {
    return this.form.controls;
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  resetErrorMessage() {
    this.loginError = false;
    this.unregisteredMail = false;
  }

  setEmailFromForgotPassword(formGroup: FormGroup): void {
    if (this.email) {
      formGroup.patchValue({
        email: this.email
      });
    }
  }

  private lowercaseFirstLetter(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  private setUserInfoIntoCookies(token: string, email: string, role: string): void {
    const expirationDateTimestamp = this.tokenUtil.getJwtTokenExpirationTimestamp(token)
    const expirationDate = new Date(expirationDateTimestamp);
    this.userService.setToken(token, expirationDate)
    this.userService.setEmail(email, expirationDate)
    this.userService.setRole(role, expirationDate)
  }

  private navigationAfterLogin(): void {
    const redirectUrl = this.userService.getRedirectUrl();
    if (redirectUrl) {
      this.userService.clearRedirectUrl();
      this.router.navigateByUrl(redirectUrl);
    } else if (this.favoriteCardId) {
      this.handleSavingFavoriteCardAndNavigation(this.favoriteCardId);
    } else if (this.isNavigateToTeamPageAfterLogin) {
      const pendingTeamId = this.invitationTokenService.getPendingTeamId();
      this.invitationTokenService.clearPendingTeamId();
      this.router.navigate(['/teams'], pendingTeamId ? { queryParams: { teamId: pendingTeamId } } : {});
    } else if (this.fromSubscriptionPage) {
      if (this.subscriptionInfoId !== null && this.isMonthly !== null) {
        this.router.navigate([`subscriptions/payment/${this.subscriptionInfoId}/${this.isMonthly}`]);
      } else {
        this.router.navigate(['/subscriptions']);
      }
    } else if (this.fromRegistration) {
      if (this.reservedIdentificationId) {
        this.router.navigate(['/prices'], {state: {reservedIdentificationId: this.reservedIdentificationId}});
      } else {
        this.router.navigate(['/prices']);
      }
    } else if (this.returnUrl) {
      this.router.navigate([this.returnUrl], {});
    } else {
      this.router.navigate(['/cards'], {state: {reservedIdentificationId: this.reservedIdentificationId}});
    }
  }

  private handleSavingFavoriteCardAndNavigation(favoriteCardId: bigint): void {
    this.favoriteCardsService.saveFavoriteCardByCardId(favoriteCardId).subscribe(() => {
      this.router.navigate(['/', this.favoriteCardCardId]);
    })
  }
}
